import React, { useRef, useState } from 'react';
import propTypes from 'prop-types';
import { showAlert } from 'state/actions/site';
import { changeUserProfilePic } from 'state/actions/user';
import { useDispatch, useSelector } from 'react-redux';
import imageCompression from 'browser-image-compression';
import BrandLogo from 'tailwind-components/BrandLogo/BrandLogo';
import { getBase64 } from 'helpers/imageProcessing';
import { getCompany } from 'helpers/getCompanyVariables';
import useHiddenMode, { maskValue } from 'hooks/useHiddenMode';
import useViewAs from 'hooks/useViewAs';
import { safePostCall } from 'helpers/firebaseSetup';
import endpoints from 'constants/endpoints';
import Card from 'tailwind-components/Card/Card';
import { Edit, Pencil } from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import useMobile from 'hooks/useMobile';
import Input from 'tailwind-components/Input/Input';
import InputWithCopyButton from 'tailwind-components/InputWithCopyButton/InputWithCopyButton';
import Separator from 'tailwind-components/Separator/Separator';
import SimpleContainer from 'tailwind-components/SimpleContainer/SimpleContainer';
import OffboardingModal from 'tailwind-components/Offboarding/OffboardingModal';
import ReleaseRequestSignModal from 'tailwind-components/Offboarding/ReleaseRequestSignModal';

const company = getCompany();

const isOneill = company === 'oneill';

export default function PersonalInformation({
  setModalText,
  setIsOpenPrimaryModal,
  refreshUser
}) {
  const user = useSelector(state => state.user.zohoData);
  const { isViewingAs } = useViewAs();
  const dispatch = useDispatch();
  const isMobile = useMobile();
  const { hidden } = useHiddenMode();

  const [isOpenOffboardModal, setIsOpenOffboardModal] = useState(false);
  const [signUrl, setSignUrl] = useState(null);
  const [isSignLoading, setIsSignLoading] = useState(false);

  const normalizeBool = val =>
    val === true ||
    val === 1 ||
    (typeof val === 'string' &&
      ['true', '1'].includes(val.trim().toLowerCase()));

  const hasAdminApproval = normalizeBool(user?.hasAdminApproval);
  const hasSecondApproval = normalizeBool(user?.hasSecondApproval);
  const secondApprovalDenied = normalizeBool(user?.secondApprovalDenied);
  const secondApprovalGranted = normalizeBool(user?.secondApprovalGranted);
  const secondApprovalHelperText = user?.secondApprovalHelperText ?? '';
  const agentSurveyCompleted = normalizeBool(user?.agentSurveyCompleted);
  const isBeingOffboarded = normalizeBool(user?.isBeingOffboarded);
  const releaseDocumentStatus = user?.releaseDocumentStatus ?? null;
  const offboardingStatus = user?.offboardingStatus ?? null;
  const isBlackoutBlocked = normalizeBool(user?.isBlackoutBlocked);
  const blackoutHelperText = user?.blackoutHelperText ?? '';
  const hasOffboardingRecord = !!user?.offboardingRecordId;
  const agentNeedsToSign = releaseDocumentStatus === 'Pending Agent Signature';
  const offboardingCompleted = offboardingStatus === 'Complete';
  const offboardingDenied = offboardingStatus === 'Denied';
  // isBeingOffboarded excludes "Not Started" (admin just created the record).
  // !!releaseDocumentStatus excludes null (agent just submitted survey, waiting for admin approval).
  // Together they only flag "in progress" when Zoho Sign is actively processing the document.
  const isInProgress =
    isBeingOffboarded && !!releaseDocumentStatus && !agentNeedsToSign;

  const showOffboardButton = isOneill && !isViewingAs;

  const fileInputRef = useRef(null);

  async function handleSign() {
    setIsSignLoading(true);
    try {
      const body = {
        isZohoRequest: true,
        endpoint: endpoints.zoho.offboardingZohoSign,
        agentId: user.id,
        agentEmail: user.Email,
        newEmployerEmail: user.newEmployerEmail,
        offboardingRecordId: user.offboardingRecordId,
        host: window.location.origin.replace('http://', 'https://')
      };
      const response = await safePostCall(endpoints.awsProxy.safePost, body);
      const output = response?.data?.details?.output;
      if (output) {
        const parsed = JSON.parse(output);
        if (parsed?.sign_url) {
          setSignUrl(parsed.sign_url);
        }
      }
    } catch (error) {
      console.error('handleSign error', error);
      dispatch(
        showAlert({
          message: 'Something went wrong. Try again later.',
          code: 'error'
        })
      );
    } finally {
      setIsSignLoading(false);
    }
  }

  async function handleSignCompleted() {
    if (refreshUser) {
      try {
        await dispatch(refreshUser(() => {}, {}));
      } catch (e) {
        console.error('Refresh after signing failed', e);
      }
    }
    setSignUrl(null);
  }

  async function setProfilePicFile(e) {
    const options = {
      maxSizeMB: 0.02,
      maxWidthOrHeight: 500,
      useWebWorker: true
    };

    const target = e.target;
    if (!target.files?.[0]?.type?.includes('image')) {
      dispatch(
        showAlert({ message: 'File not valid as Profile Pic', code: 'error' })
      );
      return;
    }

    dispatch(showAlert({ message: 'Updating Profile Photo', code: 'info' }));

    const compressedFile = await imageCompression(target.files[0], options);

    getBase64(compressedFile).then(result => {
      dispatch(
        changeUserProfilePic(
          result.split(',')[1],
          user.id,
          fileInputRef,
          // this view has no local loader or cancellable request to hand over
          () => {},
          {}
        )
      );
    });
  }

  return (
    <>
      <Card
        title='Personal Information'
        withSeparator
        separatorVariant='dashed'
      >
        <section className='grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-4'>
          <div className='flex items-center justify-center p-0 m-0'>
            <div className='w-[160px] h-[160px] lg:w-[200px] lg:h-[200px] rounded-full overflow-hidden flex items-center relative lg:mb-20 ring-1 ring-black/10'>
              <BrandLogo
                src={user?.agentPhoto}
                alt=''
                className={`w-full h-full ${user?.agentPhoto ? '' : 'h-auto'}`}
              />
              <label
                htmlFor='profilePic'
                className='absolute inset-0 bg-black/60 z-10 cursor-pointer text-white flex flex-col items-center justify-center text-base text-center px-1 opacity-0 transition-all duration-300 hover:opacity-100'
              >
                <Edit className='mb-2.5 shrink-0' size={24} />
                Edit Profile Picture
              </label>
              <input
                type='file'
                id='profilePic'
                className='hidden'
                accept='image/*'
                onChange={setProfilePicFile}
                ref={fileInputRef}
              />
            </div>
          </div>
          <div className='flex flex-col gap-4 h-full grow flex-1 justify-between'>
            <div className='grid md:grid-cols-2 gap-4 lg:gap-8'>
              <SimpleContainer>
                <h3 className='text-lg font-semibold m-0 pb-1 text-left leading-none text-grey'>
                  Agent Information
                </h3>
                <Input
                  label='First Name'
                  value={user.First_Name || ''}
                  placeholder='-'
                  disabled
                  compact
                  labelClassName='text-primary'
                />
                <Input
                  label='Last Name'
                  value={user.Last_Name || ''}
                  placeholder='-'
                  disabled
                  compact
                  labelClassName='text-primary'
                />
                <Input
                  label='Personal Email'
                  value={maskValue(user.Email || '', hidden)}
                  placeholder='-'
                  disabled
                  compact
                  labelClassName='text-primary'
                />
                <Input
                  label='Phone'
                  value={maskValue(user.Phone || '', hidden)}
                  placeholder='-'
                  disabled
                  compact
                  labelClassName='text-primary'
                />
                <Separator variant='dashed' />
                <InputWithCopyButton
                  label='NPN'
                  value={user.NPN || ''}
                  placeholder='-'
                  disabled
                  compact
                  labelClassName='text-primary'
                />
                <InputWithCopyButton
                  label='FFM ID'
                  value={user.FFMIG_Number || ''}
                  placeholder='-'
                  disabled
                  compact
                  labelClassName='text-primary'
                />
              </SimpleContainer>
              <SimpleContainer>
                <h3 className='text-lg font-semibold m-0 pb-1 text-left leading-none text-grey'>
                  Address Information
                </h3>
                <Input
                  label='Street'
                  value={user.Mailing_Street || ''}
                  placeholder='-'
                  disabled
                  compact
                  labelClassName='text-primary'
                />
                <Input
                  label='Street 2'
                  value={user.Mailing_Street_2 || ''}
                  placeholder='-'
                  disabled
                  compact
                  labelClassName='text-primary'
                />
                <Input
                  label='City'
                  value={user.Mailing_City || ''}
                  placeholder='-'
                  disabled
                  compact
                  labelClassName='text-primary'
                />
                <Input
                  label='State'
                  value={user.Mailing_State || ''}
                  placeholder='-'
                  disabled
                  compact
                  labelClassName='text-primary'
                />
                <Input
                  label='ZIP Code'
                  value={user.Mailing_Zip || ''}
                  placeholder='-'
                  disabled
                  compact
                  labelClassName='text-primary'
                />
                <Input
                  label='Country'
                  value={user.Mailing_Country || ''}
                  placeholder='-'
                  disabled
                  compact
                  labelClassName='text-primary'
                />
              </SimpleContainer>
            </div>
            <div className='flex flex-col gap-2'>
              <div className='flex items-center justify-end gap-2'>
                {showOffboardButton &&
                  !offboardingCompleted &&
                  (agentNeedsToSign ? (
                    <Button
                      variant='danger'
                      size='small'
                      text='Continue Signing'
                      isDisabled={isSignLoading}
                      onClick={handleSign}
                    />
                  ) : (
                    !isInProgress &&
                    ((agentSurveyCompleted || hasOffboardingRecord) &&
                    !offboardingDenied ? (
                      <Button
                        variant='danger'
                        size='small'
                        text='Sign Release Request'
                        isDisabled={
                          !hasAdminApproval ||
                          hasSecondApproval ||
                          isBlackoutBlocked ||
                          isSignLoading
                        }
                        onClick={handleSign}
                      />
                    ) : (
                      <Button
                        variant='danger'
                        size='small'
                        text='Request Release'
                        onClick={() => setIsOpenOffboardModal(true)}
                      />
                    ))
                  ))}
                <Button
                  variant='primary'
                  size='small'
                  fill={isMobile}
                  text='Edit'
                  icon={<Pencil size={20} />}
                  onClick={() => {
                    setModalText('Personal Information');
                    setIsOpenPrimaryModal(true);
                  }}
                />
              </div>
              {showOffboardButton &&
                (offboardingCompleted ? (
                  <p className='text-right text-sm font-medium text-green-700 m-0'>
                    Your release document has been signed. Check your email for
                    more information.
                  </p>
                ) : offboardingDenied ? (
                  <p className='text-right text-sm font-medium text-red-700 m-0'>
                    Your release request was denied. You may submit a new
                    request.
                  </p>
                ) : isInProgress ? (
                  <p className='text-right text-sm font-medium text-red-700 m-0'>
                    Your offboarding process is in progress.
                  </p>
                ) : (agentSurveyCompleted || hasOffboardingRecord) &&
                  !hasAdminApproval &&
                  !agentNeedsToSign ? (
                  <p className='text-right text-sm font-medium text-orange-600 m-0'>
                    You need admin approval to sign the release request.
                  </p>
                ) : (agentSurveyCompleted || hasOffboardingRecord) &&
                  hasAdminApproval &&
                  hasSecondApproval &&
                  !agentNeedsToSign ? (
                  <p className='text-right text-sm font-medium text-orange-600 m-0'>
                    {secondApprovalHelperText ||
                      'Awaiting second approval before you can sign.'}
                  </p>
                ) : (agentSurveyCompleted || hasOffboardingRecord) &&
                  hasAdminApproval &&
                  secondApprovalDenied &&
                  !agentNeedsToSign ? (
                  <p className='text-right text-sm font-medium text-red-700 m-0'>
                    Your release request was denied. Please contact your admin
                    for more information.
                  </p>
                ) : agentSurveyCompleted &&
                  isBlackoutBlocked &&
                  !agentNeedsToSign ? (
                  <p className='text-right text-sm font-medium text-orange-600 m-0'>
                    {blackoutHelperText ||
                      'Release document temporarily unavailable due to blackout period.'}
                  </p>
                ) : agentSurveyCompleted &&
                  hasAdminApproval &&
                  secondApprovalGranted &&
                  !agentNeedsToSign ? (
                  <p className='text-right text-sm font-medium text-green-700 m-0'>
                    Second approval granted. You can now sign the release
                    request.
                  </p>
                ) : null)}
            </div>
          </div>
        </section>
      </Card>
      {isOpenOffboardModal && (
        <OffboardingModal
          onClose={() => setIsOpenOffboardModal(false)}
          source='agentInfo'
          selectedAgentId={user.id}
          refreshUser={refreshUser}
        />
      )}
      <ReleaseRequestSignModal
        active={!!signUrl}
        signUrl={signUrl}
        onSigned={handleSignCompleted}
      />
    </>
  );
}

PersonalInformation.propTypes = {
  setModalText: propTypes.func,
  setIsOpenPrimaryModal: propTypes.func,
  refreshUser: propTypes.func
};
